/*
 * Element Brooklyn — creator/affiliate attribution capture.
 *
 * Companion to eb-attribution.js, but a different mechanism and a different job.
 * That script pings the Cloudflare Worker so it can set _eb_ft/_eb_lt. This one
 * copies the creator tokens into CART ATTRIBUTES, which is how they survive to
 * the order as note_attributes and reach Element Ops.
 *
 * Where the tokens come from
 * --------------------------
 * A creator's link points at creators.elementbrooklyn.com/<slug>, which records
 * the click server-side and 302s here with ?eb_c=<click token>&eb_cr=<affiliate
 * token>. That hop also sets two first-party cookies on .elementbrooklyn.com via
 * Set-Cookie — server-set during a first-party navigation, so they survive
 * Safari ITP (document.cookie would be capped). We therefore read the URL first
 * and fall back to the cookies on later page views.
 *
 * Two tokens, two jobs — do not collapse them:
 *   _eb_ft_creator_click  the CLICK token. Proves first-party attribution and is
 *                         what commission is paid on. The `_eb_ft_` prefix is
 *                         what ShopifyImportService already recognises as
 *                         first-party attribution data.
 *   _eb_creator           the AFFILIATE token. Read by the codeless discount
 *                         Shopify Function against its allowlist. Kept separate
 *                         so a stale or forged click token can never conjure a
 *                         discount, and revoking an affiliate is instant.
 *
 * WITHOUT THIS SCRIPT the creator programme does not work: there is no discount
 * code anywhere in the design, so if the cart attributes are missing the
 * customer gets no discount and the creator earns nothing.
 *
 * Campaign links ride the same path
 * ---------------------------------
 * A printed QR (go.elementbrooklyn.com/<CODE>, Element Ops Go::LinksController)
 * sets an `_eb_promo` cookie and redirects here WITHOUT any token in the URL.
 * This script copies the cookie into the `_eb_promo` cart attribute, which the
 * same discount Function checks against a separate promo allowlist. Cookie
 * only, by design: a landing URL with no token is a URL that can be shared
 * without sharing the discount.
 *
 * For the same reason the creator params (eb_c / eb_cr) are removed from the
 * address bar once read, so a visitor who taps Share on the page they landed on
 * doesn't pass on a link that carries the discount. The cookies set by the
 * hop keep the attribution.
 *
 * Never blocks render; every failure path is a no-op.
 */
(function () {
  var CLICK_ATTR = '_eb_ft_creator_click';
  var CREATOR_ATTR = '_eb_creator';
  var PROMO_ATTR = '_eb_promo';
  var CLICK_COOKIE = '_eb_creator';
  var CREATOR_COOKIE = '_eb_creator_ref';
  var PROMO_COOKIE = '_eb_promo';
  // The signature last verified on a cart. It only lets later page views in the
  // session skip the page-load cart.js GET; it never skips a needed write,
  // because every cartUpdate re-reads the live cart.
  //
  // Fixed 2026-09 (the bugs eb-ab.js's header describes):
  //   1. The guard used to be keyed on the VALUE only, so the new cart Shopify
  //      issues after a completed checkout never got the attributes — a second
  //      order in the same tab session lost the discount and the commission.
  //      Now every cartUpdate re-checks the live cart (GET cart.js) and rewrites
  //      whenever that cart's attributes are missing or different.
  //   2. `res.ok` was trusted. /cart/update.js returns the full cart, so we now
  //      parse it and assert both keys are present; a 200 that did not apply is
  //      retried once and never recorded as synced.
  //   3. The first-pageview write can race ShopMy's own cart write (both create
  //      a cart when none exists; only one cart cookie survives). After a
  //      landing write we re-check once, after ShopMy's retry window, and
  //      rewrite if the surviving cart lacks our attributes.
  // The landing-page write itself is kept on purpose: the discount Function
  // reads _eb_creator, so it must be on the cart before the first add or the
  // drawer shows an undiscounted price.
  var SYNCED_KEY = 'eb_creator_synced';
  var RECHECK_MS = 3000;

  var click = '';
  var creator = '';
  var promo = '';
  var signature = '';
  var attributes = {};
  var inFlight = false;
  var pending = false; // a cartUpdate arrived mid-sync; re-run once it finishes

  try {
    var params = new URLSearchParams(window.location.search);
    click = params.get('eb_c') || readCookie(CLICK_COOKIE);
    creator = params.get('eb_cr') || readCookie(CREATOR_COOKIE);
    promo = readCookie(PROMO_COOKIE);
    stripTokenParams(params);

    if (!click && !creator && !promo) return;

    signature = (click || '') + '|' + (creator || '') + '|' + (promo || '');
    if (click) attributes[CLICK_ATTR] = click;
    if (creator) attributes[CREATOR_ATTR] = creator;
    if (promo) attributes[PROMO_ATTR] = promo;

    // Page load: only when this signature has not been verified on a cart yet
    // this session (i.e. the landing page, or a new token pair). Later page
    // views cost no cart requests; new carts are caught by cartUpdate below.
    if (safeSessionGet(SYNCED_KEY) !== signature) {
      sync(0, function (wrote) {
        if (wrote) setTimeout(function () { sync(0); }, RECHECK_MS);
      });
    }

    // Re-check on every cart change — this is what catches the fresh cart after
    // a completed checkout. `subscribe`/PUB_SUB_EVENTS are bare globals from
    // pubsub.js/constants.js (a top-level const is NOT on window).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { sync(0); });
    }
  } catch (e) {
    /* swallow — never block render */
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function hasAttributes(cart) {
    if (!cart || !cart.attributes) return false;
    for (var k in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, k) && cart.attributes[k] !== attributes[k]) return false;
    }
    return true;
  }

  function markSynced() {
    safeSessionSet(SYNCED_KEY, signature);
  }

  // done(wrote) is called once with true only when a write was verified.
  function sync(attempt, done) {
    done = done || function () {};
    if (inFlight) {
      pending = true;
      return done(false);
    }
    inFlight = true;

    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return false; // 401/429/5xx: leave the guards unset so a later event retries
        if (hasAttributes(cart)) {
          markSynced();
          return false;
        }

        return fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: attributes }),
          keepalive: true
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (updated) {
            if (hasAttributes(updated)) {
              markSynced();
              return true;
            }
            // A failed or 200-but-not-applied write. Retry once, then leave the
            // guards unset so the next cartUpdate / page load tries again.
            if (!attempt) {
              inFlight = false;
              sync(1, done);
              return null; // the retry owns done()
            }
            return false;
          });
      })
      .catch(function () { return false; /* attribution must never break the storefront */ })
      .then(function (wrote) {
        if (wrote === null) return;
        inFlight = false;
        done(!!wrote);
        if (pending) {
          pending = false;
          sync(0);
        }
      });
  }

  // Already read into variables above; the cookies carry them from here on.
  function stripTokenParams(params) {
    if (!params.has('eb_c') && !params.has('eb_cr')) return;
    try {
      params.delete('eb_c');
      params.delete('eb_cr');
      var query = params.toString();
      window.history.replaceState(window.history.state, '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash);
    } catch (e) { /* ignore — cosmetic */ }
  }

  function readCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  // Private mode / disabled storage must not throw.
  function safeSessionGet(k) {
    try { return window.sessionStorage.getItem(k); } catch (e) { return null; }
  }
  function safeSessionSet(k, v) {
    try { window.sessionStorage.setItem(k, v); } catch (e) { /* ignore */ }
  }
})();
